/**
 * Notifications REST API (list, mark read, push token) and SSE stream URL.
 */

import apiClient from './api-client';
import { getApiV1BaseUrlFromEnv } from '@/lib/apiBaseUrl';
import type { AppNotification, PaginatedNotifications } from '@/types/notification.types';

/** SSE URL for `GET /notifications/stream?token=` (EventSource cannot set headers). */
export function notificationsStreamUrl(token: string): string {
  const base = getApiV1BaseUrlFromEnv(import.meta.env.VITE_API_URL);
  if (!base) return '';
  return `${base}/notifications/stream?token=${encodeURIComponent(token)}`;
}

/** Notifications REST helpers (list, mark read, push token). */
export const notificationsApi = {
  /** `GET /notifications` */
  list: (page = 1, pageSize = 20, unreadOnly = false): Promise<PaginatedNotifications> =>
    apiClient.get('/notifications', { params: { page, pageSize, unreadOnly } }).then((r) => r.data),

  /** `GET /notifications/unread-count` */
  unreadCount: (): Promise<number> =>
    apiClient.get('/notifications/unread-count').then((r) => r.data?.count ?? 0),

  /** `PATCH /notifications/:id/read` */
  markRead: (id: string): Promise<AppNotification> =>
    apiClient.patch(`/notifications/${id}/read`).then((r) => r.data),

  /** `POST /notifications/read-all` */
  markAllRead: (): Promise<void> => apiClient.post('/notifications/read-all').then(() => undefined),

  /** `POST /notifications/push-tokens` */
  registerPushToken: (token: string): Promise<void> =>
    apiClient.post('/notifications/push-tokens', { token }).then(() => undefined),

  /** `DELETE /notifications/push-tokens` */
  unregisterPushToken: (token: string): Promise<void> =>
    apiClient.delete('/notifications/push-tokens', { data: { token } }).then(() => undefined),
};
